import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { LogOut, Menu, Search, Server } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserAvatar } from "@/components/user/UserAvatar";
import { API_BASE } from "@/lib/api";
import { SidebarBrand, SidebarNav } from "./Sidebar";

interface TopbarProps {
  user: { id: string; email: string; name?: string | null; image?: string | null } | null;
  onSignOut: () => void;
}

// VITE_API_URL is deliberately empty in production too (the SPA calls /api on
// its own origin), so the build mode, not the URL, says where we are. Every
// environment ends up on the production database (see CLAUDE.md).
function envInfo(): { label: string; upstream: string; tone: "error" | "warn" } {
  if (API_BASE) return { label: "custom", upstream: API_BASE, tone: "warn" };
  if (import.meta.env.PROD) return { label: "prod", upstream: "/api → Pages proxy → production Worker", tone: "error" };
  return { label: "local → prod db", upstream: "/api → vite proxy → localhost:8787 (production database)", tone: "warn" };
}

export function Topbar({ user, onSignOut }: TopbarProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const env = envInfo();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the mobile menu on navigation (including the search box's navigate())
  // and when the window widens to the desktop layout, where the sidebar shows.
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => {
      if (mq.matches) setMenuOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const handleSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    const q = (e.currentTarget.value ?? "").trim();
    if (!q) return;
    navigate(`/users?search=${encodeURIComponent(q)}`);
  };

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border/60 bg-background/85 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <DialogPrimitive.Root open={menuOpen} onOpenChange={setMenuOpen}>
        <DialogPrimitive.Trigger asChild>
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open navigation">
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
        </DialogPrimitive.Trigger>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-950/80" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed inset-y-0 left-0 z-50 flex w-64 max-w-[85vw] flex-col overflow-y-auto border-r border-border/60 bg-card focus:outline-none"
          >
            <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
            <SidebarBrand onNavigate={() => setMenuOpen(false)} />
            <SidebarNav onNavigate={() => setMenuOpen(false)} />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      <div className="relative max-w-md flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label="Search users"
          placeholder="Search users by email, name, or id… (Enter)"
          className="h-8 pl-8 text-sm"
          onKeyDown={handleSearchKey}
        />
      </div>
      <Badge variant={env.tone} className="hidden gap-1 font-mono uppercase sm:inline-flex">
        <Server className="h-3 w-3" aria-hidden="true" />
        {env.label}
      </Badge>
      <span className="hidden font-mono text-[10px] text-muted-foreground lg:inline">
        {env.upstream}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 gap-2 px-2">
            <UserAvatar size="sm" name={user?.name} email={user?.email} image={user?.image} />
            <span className="hidden text-xs sm:inline">{user?.email ?? "—"}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{user?.email ?? "Not signed in"}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onSignOut}>
            <LogOut className="h-4 w-4" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
